import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import ProjectTimeline from './ProjectTimeline';
import TaskModal from '../Tasks/TaskModal';
import * as FiIcons from 'react-icons/fi';

const { 
  FiX, FiCalendar, FiUsers, FiBriefcase, FiCheckSquare, 
  FiPieChart, FiFileText, FiActivity, FiAlertTriangle, FiDollarSign, 
  FiPaperclip, FiMessageCircle, FiSend, FiPlus, FiUpload
} = FiIcons;

const overlayVariants = {
  hidden: { opacity: 0, pointerEvents: 'none' },
  visible: { opacity: 1, pointerEvents: 'auto' },
  exit: { opacity: 0, pointerEvents: 'none' }
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 }
};

const ProjectDetails = ({ project, onClose }) => {
  const { projects, teamMembers, clients, tasks, projectMessages, addProjectMessage, addProjectFile } = useData();
  
  // Use live project data from context to ensure updates (like file uploads) show immediately
  const liveProject = projects.find(p => p.id === project.id) || project;
  
  const [activeTab, setActiveTab] = useState('overview');
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const assignedMembers = liveProject.teamIds.map(id => teamMembers.find(m => m.id === id)).filter(Boolean);
  const client = clients.find(c => c.id === liveProject.clientId);
  const projectTasks = tasks.filter(t => t.projectId === liveProject.id);
  const messages = projectMessages.filter(m => m.projectId === liveProject.id).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const daysLeft = differenceInDays(new Date(liveProject.deadline), new Date());

  const tabs = [
    { id: 'overview', icon: FiActivity, label: 'Overview' },
    { id: 'timeline', icon: FiCalendar, label: 'Timeline' },
    { id: 'tasks', icon: FiCheckSquare, label: 'Tasks' },
    { id: 'chat', icon: FiMessageCircle, label: 'Team Chat' },
    { id: 'budget', icon: FiPieChart, label: 'Budget & Risks' },
    { id: 'files', icon: FiPaperclip, label: 'Files' }
  ];

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (newMessage.trim()) {
      addProjectMessage(liveProject.id, newMessage.trim());
      setNewMessage('');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      addProjectFile(liveProject.id, file);
    }
  };

  useEffect(() => {
    if (activeTab === 'chat' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  return (
    <motion.div
      variants={overlayVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Close only if clicking the backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ duration: 0.2 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            <div className="bg-blue-600 p-3 rounded-xl shadow-lg shadow-blue-200">
              <SafeIcon icon={FiBriefcase} className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{liveProject.title}</h2>
              <div className="flex items-center space-x-3 mt-1 text-sm text-gray-500">
                <span>{client ? client.name : 'Internal Project'}</span>
                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                <span className={`${daysLeft < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                  {daysLeft < 0 ? 'Overdue' : `${daysLeft} days remaining`}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 px-6 bg-white sticky top-0 z-10 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-colors relative whitespace-nowrap ${
                activeTab === tab.id ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
              {activeTab === tab.id && (
                <motion.div 
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
                />
              )}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-white">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                {/* Description */}
                <div className="prose max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-600 leading-relaxed">{liveProject.description}</p>
                </div>

                {/* Progress */}
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                  <div className="flex justify-between items-end mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Overall Progress</h3>
                      <p className="text-sm text-gray-500">Based on task completion and milestones</p>
                    </div>
                    <span className="text-3xl font-bold text-blue-600">{liveProject.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-1000" 
                      style={{ width: `${liveProject.progress}%` }}
                    ></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Tasks</div>
                      <div className="font-semibold text-gray-900">{projectTasks.length}</div>
                    </div>
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Completed</div>
                      <div className="font-semibold text-green-600">
                        {projectTasks.filter(t => t.status === 'completed').length}
                      </div>
                    </div>
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <div className="text-xs text-gray-500 uppercase tracking-wide">Pending</div>
                      <div className="font-semibold text-yellow-600">
                        {projectTasks.filter(t => t.status !== 'completed').length}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                 {/* Team */}
                 <div>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4 flex items-center">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 mr-2" />
                    Project Team
                  </h3>
                  <div className="space-y-3">
                    {assignedMembers.map(member => (
                      <div key={member.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg transition-colors border border-transparent hover:border-gray-100">
                        <img src={member.avatar} alt={member.name} className="w-10 h-10 rounded-full" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{member.name}</p>
                          <p className="text-xs text-gray-500">{member.role.replace('_', ' ')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Key Details */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-sm space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize 
                      ${liveProject.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {liveProject.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Start Date</span>
                    <span className="text-gray-900 font-medium">
                      {liveProject.startDate ? format(new Date(liveProject.startDate), 'MMM dd, yyyy') : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Deadline</span>
                    <span className="text-gray-900 font-medium">
                      {format(new Date(liveProject.deadline), 'MMM dd, yyyy')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'timeline' && (
            <ProjectTimeline 
              milestones={liveProject.milestones || []} 
              startDate={liveProject.startDate}
              deadline={liveProject.deadline}
            />
          )}

          {activeTab === 'tasks' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Project Tasks</h3>
                  <span className="text-sm text-gray-500">{projectTasks.length} tasks total</span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowTaskModal(true)}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  <span>Add Task</span>
                </motion.button>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {projectTasks.length > 0 ? projectTasks.map(task => (
                  <div key={task.id} className="p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow bg-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">{task.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        task.status === 'completed' ? 'bg-green-100 text-green-700' : 
                        task.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {task.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl border-dashed border-2 border-gray-200">
                    <SafeIcon icon={FiCheckSquare} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No tasks assigned to this project yet.</p>
                    <button 
                      onClick={() => setShowTaskModal(true)}
                      className="text-blue-600 text-sm font-medium hover:underline mt-2"
                    >
                      Create your first task
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'chat' && (
            <div className="flex flex-col h-full h-[500px]">
              <div className="flex-1 overflow-y-auto space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200 mb-4">
                {messages.length > 0 ? (
                  messages.map(msg => {
                    const user = teamMembers.find(m => m.id === msg.userId);
                    return (
                      <div key={msg.id} className="flex space-x-3">
                        <img src={user?.avatar} alt={user?.name} className="w-8 h-8 rounded-full flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-baseline space-x-2">
                            <span className="font-semibold text-sm text-gray-900">{user?.name}</span>
                            <span className="text-xs text-gray-500">{formatDistanceToNow(new Date(msg.createdAt), { addSuffix: true })}</span>
                          </div>
                          <div className="bg-white p-3 rounded-tr-lg rounded-br-lg rounded-bl-lg shadow-sm mt-1 text-sm text-gray-700 border border-gray-100">
                            {msg.text}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <SafeIcon icon={FiMessageCircle} className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Start the discussion!</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              <form onSubmit={handleSendMessage} className="relative">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message to the team..."
                  className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                </button>
              </form>
            </div>
          )}

          {activeTab === 'budget' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Budget Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Budget Tracking</h3>
                </div>
                
                {liveProject.budget ? (
                  <div className="space-y-6">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-sm text-gray-500">Total Budget</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {liveProject.budget.currency} {liveProject.budget.total.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">Remaining</p>
                        <p className="text-xl font-semibold text-green-600">
                          {liveProject.budget.currency} {(liveProject.budget.total - liveProject.budget.spent).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Spent: {liveProject.budget.currency} {liveProject.budget.spent.toLocaleString()}</span>
                        <span>{Math.round((liveProject.budget.spent / liveProject.budget.total) * 100)}% Used</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div 
                          className={`h-4 rounded-full ${
                            (liveProject.budget.spent / liveProject.budget.total) > 0.9 ? 'bg-red-500' :
                            (liveProject.budget.spent / liveProject.budget.total) > 0.7 ? 'bg-yellow-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, (liveProject.budget.spent / liveProject.budget.total) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No budget data set.</p>
                )}
              </div>

              {/* Risks Section */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Risk Assessment</h3>
                </div>

                <div className="space-y-4">
                  {liveProject.risks && liveProject.risks.length > 0 ? liveProject.risks.map(risk => (
                    <div key={risk.id} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-medium text-gray-900">{risk.title}</h4>
                        <span className={`text-xs px-2 py-1 rounded-full uppercase font-bold ${
                          risk.impact === 'high' ? 'bg-red-100 text-red-700' :
                          risk.impact === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {risk.impact} Impact
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <span>Probability: <span className="capitalize">{risk.probability}</span></span>
                        <span>Status: <span className="capitalize">{risk.status}</span></span>
                      </div>
                    </div>
                  )) : (
                    <p className="text-gray-500 italic">No risks identified.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'files' && (
            <div>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Project Files</h3>
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  onChange={handleFileUpload} 
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => fileInputRef.current.click()}
                  className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <SafeIcon icon={FiUpload} className="w-4 h-4" />
                  <span>Upload File</span>
                </motion.button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {liveProject.files && liveProject.files.length > 0 ? liveProject.files.map(file => (
                  <div key={file.id} className="p-4 border border-gray-200 rounded-xl hover:border-blue-500 transition-colors group cursor-pointer bg-gray-50">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white rounded-lg shadow-sm">
                        <SafeIcon icon={FiFileText} className="w-6 h-6 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">{file.size} • {format(new Date(file.uploadDate), 'MMM dd')}</p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="col-span-full text-center py-12 bg-gray-50 rounded-xl border-dashed border-2 border-gray-200">
                    <SafeIcon icon={FiPaperclip} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No files available.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </motion.div>
      
      {/* Nested Task Modal */}
      <AnimatePresence>
        {showTaskModal && (
          <TaskModal 
            onClose={() => setShowTaskModal(false)}
            initialData={{ projectId: liveProject.id, clientId: liveProject.clientId }}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProjectDetails;